import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Eye, Lock, LockOpen, ShieldCheck, ShieldAlert } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  type AccountSummary,
  useExportSession,
  useLock2FA,
  useReveal2FA,
  useSet2FAPassword,
  useTerminateOtherSessions,
} from "@/lib/accounts";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { ConfirmDialog } from "@/components/ui/dialog";
import { pushToast } from "@/components/Toaster";

/**
 * AccountSecurityTab — 2FA-lock card + terminate-other-sessions card
 * (DESIGN-SYSTEM §6.4: confirms via ConfirmDialog, results via toast).
 */
export function AccountSecurityTab({ account }: { account: AccountSummary }) {
  return (
    <div className="space-y-4">
      <TwoFACard account={account} />
      <TerminateSessionsCard account={account} />
    </div>
  );
}

function TwoFACard({ account }: { account: AccountSummary }) {
  const { t } = useTranslation();
  const lock = useLock2FA();
  const reveal = useReveal2FA();
  const setPwd = useSet2FAPassword();
  const [error, setError] = useState<string | null>(null);
  const [shownPassword, setShownPassword] = useState<string | null>(null);
  const [showSellerInput, setShowSellerInput] = useState(false);
  const [sellerPwd, setSellerPwd] = useState("");
  const [showManualEdit, setShowManualEdit] = useState(false);
  const [manualPwd, setManualPwd] = useState("");
  const [confirmLock, setConfirmLock] = useState(false);

  const isLocked = !!account.tg_2fa_locked_at;

  const onLock = async () => {
    setError(null);
    setShownPassword(null);
    try {
      const res = await lock.mutateAsync({
        id: account.id,
        current_password: sellerPwd.trim() || undefined,
      });
      setShownPassword(res.password);
      setSellerPwd("");
      setShowSellerInput(false);
      pushToast({ kind: "success", text: t("accounts.lock_2fa.locked") });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  const onReveal = async () => {
    setError(null);
    try {
      const res = await reveal.mutateAsync(account.id);
      setShownPassword(res.password);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  const onManualSave = async () => {
    setError(null);
    const value = manualPwd.trim();
    if (!value) return;
    try {
      const res = await setPwd.mutateAsync({ id: account.id, password: value });
      setShownPassword(res.password);
      setManualPwd("");
      setShowManualEdit(false);
      pushToast({ kind: "success", text: t("accounts.lock_2fa.manual_save") });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {isLocked ? (
            <ShieldCheck size={18} strokeWidth={2} className="text-success" aria-hidden="true" />
          ) : (
            <ShieldAlert size={18} strokeWidth={2} className="text-warning" aria-hidden="true" />
          )}
          {isLocked ? t("accounts.lock_2fa.locked") : t("accounts.lock_2fa.unlocked")}
        </CardTitle>
        <p className="text-sm text-fg-muted">
          {isLocked
            ? t("accounts.lock_2fa.locked_hint", {
                date: formatDateTime(account.tg_2fa_locked_at!),
              })
            : t("accounts.lock_2fa.unlocked_hint")}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {isLocked ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={onReveal}
                loading={reveal.isPending}
                iconLeft={<Eye size={15} strokeWidth={2} aria-hidden="true" />}
              >
                {t("accounts.lock_2fa.reveal")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowManualEdit((v) => !v)}>
                {showManualEdit
                  ? t("accounts.lock_2fa.hide_manual")
                  : t("accounts.lock_2fa.edit_manual")}
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={() => setShowSellerInput((v) => !v)}>
                {showSellerInput
                  ? t("accounts.lock_2fa.hide_seller")
                  : t("accounts.lock_2fa.has_seller_pwd")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowManualEdit((v) => !v)}>
                {showManualEdit
                  ? t("accounts.lock_2fa.hide_manual")
                  : t("accounts.lock_2fa.save_manual")}
              </Button>
              <Button
                size="sm"
                onClick={() => setConfirmLock(true)}
                loading={lock.isPending}
                iconLeft={<Lock size={15} strokeWidth={2} aria-hidden="true" />}
              >
                {t("accounts.lock_2fa.lock_now")}
              </Button>
            </>
          )}
        </div>

        {showManualEdit && (
          <div className="space-y-1.5 border-t border-border pt-3">
            <Label htmlFor={`manual-pwd-${account.id}`}>
              {t("accounts.lock_2fa.manual_label")}
            </Label>
            <div className="flex gap-2">
              <Input
                id={`manual-pwd-${account.id}`}
                type="text"
                value={manualPwd}
                onChange={(e) => setManualPwd(e.target.value)}
                placeholder={t("accounts.lock_2fa.manual_placeholder") ?? ""}
              />
              <Button
                size="sm"
                onClick={onManualSave}
                loading={setPwd.isPending}
                disabled={!manualPwd.trim()}
              >
                {t("accounts.lock_2fa.manual_save")}
              </Button>
            </div>
            <p className="text-xs text-fg-muted">{t("accounts.lock_2fa.manual_hint")}</p>
          </div>
        )}

        {!isLocked && showSellerInput && (
          <div className="space-y-1.5">
            <Label htmlFor={`seller-pwd-${account.id}`}>
              {t("accounts.lock_2fa.seller_pwd_label")}
            </Label>
            <Input
              id={`seller-pwd-${account.id}`}
              type="text"
              value={sellerPwd}
              onChange={(e) => setSellerPwd(e.target.value)}
              placeholder={t("accounts.lock_2fa.seller_pwd_placeholder") ?? ""}
            />
            <p className="text-xs text-fg-muted">{t("accounts.lock_2fa.seller_pwd_hint")}</p>
          </div>
        )}

        {shownPassword && (
          <Alert tone="info" icon={false}>
            <p className="mb-1 text-xs text-fg-muted">{t("accounts.lock_2fa.password_label")}</p>
            <code className="select-all break-all font-mono text-sm">{shownPassword}</code>
            <p className="mt-1 text-xs text-fg-muted">
              {t("accounts.lock_2fa.password_save_hint")}
            </p>
          </Alert>
        )}

        {error && <p className="break-all text-xs text-danger">{error}</p>}
      </CardContent>

      <ConfirmDialog
        open={confirmLock}
        onOpenChange={setConfirmLock}
        title={t("accounts.lock_2fa.confirm")}
        confirmText={t("accounts.lock_2fa.confirm_btn")}
        cancelText={t("common.cancel")}
        onConfirm={onLock}
      />
    </Card>
  );
}

function TerminateSessionsCard({ account }: { account: AccountSummary }) {
  const { t } = useTranslation();
  const terminate = useTerminateOtherSessions();
  const exportSession = useExportSession();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onExport = async () => {
    setError(null);
    setMsg(null);
    try {
      const data = await exportSession.mutateAsync(account.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tg-session-${data.tg_user_id ?? account.id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      pushToast({ kind: "success", text: t("accounts.export_session.ok") });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  const onTerminate = async () => {
    setError(null);
    setMsg(null);
    try {
      const res = await terminate.mutateAsync(account.id);
      if (res.status === "terminated") {
        setMsg(t("accounts.terminate_sessions.ok", { count: res.terminated_count }));
      } else if (res.status === "delayed_24h") {
        setMsg(t("accounts.terminate_sessions.delayed"));
      } else if (res.status === "none") {
        setMsg(t("accounts.terminate_sessions.none"));
      } else {
        setMsg(t("accounts.terminate_sessions.failed", { reason: res.error ?? "" }));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("accounts.terminate_sessions.title")}</CardTitle>
        <p className="text-sm text-fg-muted">{t("accounts.terminate_sessions.hint")}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setConfirmOpen(true)}
            loading={terminate.isPending}
            iconLeft={<LockOpen size={15} strokeWidth={2} aria-hidden="true" />}
          >
            {t("accounts.terminate_sessions.run")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onExport}
            loading={exportSession.isPending}
            iconLeft={<Download size={15} strokeWidth={2} aria-hidden="true" />}
          >
            {t("accounts.export_session.run")}
          </Button>
        </div>
        {msg && <p className="text-sm text-fg-secondary">{msg}</p>}
        {error && <p className="break-all text-xs text-danger">{error}</p>}
      </CardContent>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("accounts.terminate_sessions.confirm")}
        confirmText={t("accounts.terminate_sessions.confirm_btn")}
        cancelText={t("common.cancel")}
        onConfirm={onTerminate}
      />
    </Card>
  );
}
